import React, { useEffect, useState, useRef, useCallback } from 'react';
import { adminProductApi } from '../../../../api/adminProductApi';
import { getApiErrorText } from '../../../../utils/apiResponse';

export const parseApiError = getApiErrorText;

/** Однакова нормалізація для порівняння з БД і значення з інпута */
export const normalizeBarcode = (x) => {
  if (x == null || x === '') return '';
  return String(x).replace(/\r/g, '').replace(/\n/g, '').trim();
};

/** Пауза після останнього символу перед збереженням на сервер (мс). */
const BARCODE_SAVE_DEBOUNCE_MS = 400;

/**
 * Поле штрихкоду для сканера. wrapper: 'td' (таблиця) або 'div' (мобільні картки).
 * Збереження: після вводу з затримкою (debounce); при втраті фокусу — одразу.
 */
const BarcodeScanField = ({
  product,
  onBarcodeSaved,
  onBarcodeError,
  wrapper = 'td',
  wrapperClassName = '',
  inputClassName = 'form-control form-control-sm barcode-scan-input'
}) => {
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  /** Після успішного збереження робимо blur(); handleBlur не повинен знову викликати commit (stale product.barcode). */
  const skipBlurCommitRef = useRef(false);
  const [focused, setFocused] = useState(false);
  const [val, setVal] = useState(() => (product.barcode != null ? String(product.barcode) : ''));

  useEffect(() => {
    if (!focused) {
      setVal(product.barcode != null ? String(product.barcode) : '');
    }
  }, [product.barcode, product.id, focused]);

  const commit = useCallback(
    async (raw) => {
      const next = normalizeBarcode(raw);
      const prev = normalizeBarcode(product.barcode);
      if (next === prev) return;
      try {
        await adminProductApi.patchProductBarcode(product.id, next === '' ? null : next);
        onBarcodeSaved(product.id, next === '' ? null : next);
        skipBlurCommitRef.current = true;
        setFocused(false);
        requestAnimationFrame(() => {
          try {
            inputRef.current?.blur();
          } catch (e) {
            /* ignore */
          }
        });
        setTimeout(() => {
          skipBlurCommitRef.current = false;
        }, 100);
      } catch (err) {
        onBarcodeError(parseApiError(err));
        setVal(product.barcode != null ? String(product.barcode) : '');
      }
    },
    [product.barcode, product.id, onBarcodeSaved, onBarcodeError]
  );

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const scheduleCommit = useCallback(() => {
    clearDebounceTimer();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const raw = inputRef.current != null ? inputRef.current.value : '';
      commit(raw);
    }, BARCODE_SAVE_DEBOUNCE_MS);
  }, [clearDebounceTimer, commit]);

  useEffect(() => () => clearDebounceTimer(), [clearDebounceTimer]);

  const handleFocus = () => {
    setFocused(true);
    const had = normalizeBarcode(product.barcode).length > 0;
    if (had) {
      requestAnimationFrame(() => {
        try {
          inputRef.current?.select();
        } catch (e) {
          /* ignore */
        }
      });
    }
  };

  const handleBlur = () => {
    setFocused(false);
    clearDebounceTimer();
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }
    const fromInput = inputRef.current != null ? inputRef.current.value : val;
    commit(fromInput);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setVal(v);
    scheduleCommit();
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    clearDebounceTimer();
    setVal('');
    commit('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const input = (
    <input
      ref={inputRef}
      type='text'
      inputMode='numeric'
      autoComplete='off'
      spellCheck={false}
      className={inputClassName}
      style={{ fontSize: '0.875rem' }}
      value={val}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onDoubleClick={handleDoubleClick}
      onClick={(e) => e.stopPropagation()}
      title='Клік — у фокус для сканера. Збереження після паузи у вводі або при виході з поля. Подвійний клік — очистити.'
    />
  );

  if (wrapper === 'div') {
    return (
      <div
        className={wrapperClassName}
        onClick={() => inputRef.current?.focus()}
        role='presentation'
      >
        {input}
      </div>
    );
  }

  return (
    <td
      className={wrapperClassName || 'text-monospace small p-1 align-middle'}
      style={{ minWidth: '10rem', maxWidth: '16rem' }}
      onClick={() => inputRef.current?.focus()}
    >
      {input}
    </td>
  );
};

export default BarcodeScanField;
